import {
  useState,
  useMemo,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
} from 'react';
import { FloatingIconButton, Icon } from '@/components';
import { DevModeDetector } from '@/shell/DevModeDetector/DevModeDetector';
import { SearchBar } from '@/views/map/SearchBar/SearchBar';
import { getAppConfig } from '@/app/config';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { MapView } from './MapView';
import type CustomMapView from './map/CustomMapView';
import type { DeckGLRef } from '@deck.gl/react';
import { InternalReleaseBanner } from './InternalReleaseBanner';
import type {
  TreesAggregateData,
  EncroachmentLayerConfig,
  RiskTypeConfig,
} from './map/layers/types';
import { LayerType, TREE_COVER_LAYERS } from './map/layers/types';
import { Button, Overlay } from '@mantine/core';
import {
  closeFilterForm,
  openFilterForm,
  updateInspectMode,
  updateViewState,
} from '@/features/analysisMapSlice';
import { useShowMapOnSpanSelect } from '@/hooks/useShowMapOnSpanSelect';
import { ViewModeOptions } from '@/features/types';
import Filter from './filter/Filter';
import { useTrackFilterResults } from './filter/useTrackFilterResults';
import { useUserClientContext } from '@/app/clientContext';
import { useDatasets } from '@/hooks/useDatasets';
import { MapPopover } from './map/popover/MapPopover';
import type { BoundingBoxCoordinatesReturn } from './map/deckglUtils';
import {
  DESKTOP_VIEWBOX_PADDING,
  getBoundingBoxCoordinates,
  getGeometryPositions,
  getInitialViewState,
  MOBILE_VIEWBOX_PADDING,
  parseUrlViewState,
  updateViewport,
} from './map/deckglUtils';
import { useItemSelection } from './map/ItemSelection';
import { PageLayout } from './PageLayout';
import { MapLegend } from '@/views/map/MapLayerManager/MapLayerGroupControl/MapLayerOption/MapLegend';
import { getCentroid } from './map/utils';
import {
  useNarrowScreen,
  useSmallScreen,
  useWindowDimensions,
} from '@/hooks/useWindowDimension';
import { useTouch } from '@/hooks/useTouch';
import TagManager from 'react-gtm-module';
import {
  getGoogleEventForAnalysis,
  GoogleEvents,
} from '@/features/googleEvents';
import { AnalysisList } from './list/analysisList/AnalysisList';
import { ObjectiveDropdown } from './list/analysisList/ObjectiveDropdown';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import type { Coordinates, LngLatBase } from './map/types';
import { ADDRESS_PATH } from '@/app/constants';
import { FilterButtonsNetworkGroups } from './filter/FilterButtonsNetworkGroups';
import type { Feature } from 'geojson';
import { Search, X } from 'react-feather';
import { useAppFlags } from '@/hooks/useAppFlags';
import CustomListDetailPane from './list/custom-lists/CustomListDetailPane';
import CustomListOverviewPane from './list/custom-lists/CustomListOverviewPane';
import { useUrlSync } from './useUrlSync';
import { useResetOnContextChange } from './useResetOnContextChange';
import { type RedFlagWarning } from '@/features/wildfireSlice';
import { useSelectInitialNetworkGroup } from '@/hooks/useSelectInitialNetworkGroup';
import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import { FilterButton } from '@/views/filter/FilterButton';
import { FeatureBar } from '@/views/feature-bar/FeatureBar';
import { useMobileInspectMode } from '@/hooks/useMobileInspectMode';
import { useNavigateWithSearch } from '@/hooks/useNavigateWithSearch';
import { useSatelliteImageMetadata } from '@/hooks/useSatelliteImageMetadata';
import { isInProjectUrl } from '@/helpers/url';

import classes from './Analysis.module.css';
import { StatusBar } from '@/views/map/StatusBar/StatusBar';
import { ActionBar } from '@/views/action-bar/ActionBar';
import { WorklogAddOrEdit } from '@/views/map/worklog/WorkLogAddOrEdit/WorklogAddOrEdit';
import { SpanDetailSidebar } from '@/views/list/SpanDetailSidebar';

export const Analysis = () => {
  useTrackFilterResults();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const navigateWithSearch = useNavigateWithSearch();
  const dispatch = useAppDispatch();
  const { flagRedFlagWarning } = useAppFlags();

  useUrlSync();
  useResetOnContextChange();
  useSelectInitialNetworkGroup();

  const { satelliteImageMetadata, pickAndSetSatelliteMetadata } =
    useSatelliteImageMetadata();

  const {
    isListView,
    currentViewMode,
    selectedItem,
    selectedAnalysis,
    activeGroupName,
    viewState,
    mapState,
  } = useAppSelector((s) => s.analysisMap);

  const { context } = useUserClientContext();
  const { datasets } = useDatasets();

  const layerConfig = useAppSelector((s) => s.mapLayer);
  const intl = useClientTerminology();

  const [openLayerManager, setOpenLayerManager] = useState<boolean>(false);

  const initialHeight = 400;
  const [popupHeight, setPopupHeight] = useState(initialHeight);
  const [customerMapBbox, _] = useState(getInitialViewState()?.bbox);

  const { width, height } = useWindowDimensions();
  const isSmallScreen = useSmallScreen();
  const isNarrowScreen = useNarrowScreen();
  const isTouchDevice = useTouch();

  const [treesData, setTreesData] = useState<Feature[]>([]);
  const [treesAggregateData, setTreesAggregateData] = useState<
    TreesAggregateData[]
  >([]);
  const [satelliteMetadata, setSatelliteMetadata] = useState<Feature[]>([]);
  const [contextMenu, setContextMenu] = useState<Coordinates>(undefined);
  const [pageDimensions, setPageDimensions] = useState<{
    x: number;
    y: number;
  }>();
  const [locationPin, setLocationPin] = useState<LngLatBase | undefined>(
    undefined
  );
  const [redFlagWarnings, setRedFlagWarnings] = useState<
    RedFlagWarning[] | undefined
  >();

  const isMobileInspectMode = useMobileInspectMode();

  /** True when a span is selected on desktop — controls whether the
   *  SpanDetailSidebar replaces the list pane in the grid layout. */
  const isSpanSelectedOnDesktop =
    !isSmallScreen &&
    !!selectedItem?.layer?.id &&
    selectedItem.layer.id === LayerType[LayerType.scan];

  /** On mobile, auto-switch from list to map view when a span is tapped
   *  so the drawer opens over the map instead of overlaying the list. */
  useShowMapOnSpanSelect();

  if (location.pathname.match(/\/level3\//)) {
    const newPath = location.pathname.replace('level3', 'group');
    navigate({ pathname: newPath, search: location.search });
  }

  const hasLayer = useCallback(
    (layerType: LayerType) =>
      datasets.findIndex((d) => d.layerType === LayerType[layerType]) !== -1,
    [datasets]
  );

  const shouldShowRedFlagWarnings = useMemo(() => {
    return hasLayer(LayerType.wildfire_risk) && flagRedFlagWarning;
  }, [hasLayer, flagRedFlagWarning]);

  const selectedTreeCover =
    LayerType[
      TREE_COVER_LAYERS.find((l) => hasLayer(l) && layerConfig[l].visible) ??
        TREE_COVER_LAYERS.find((l) => hasLayer(l)) ??
        LayerType.encroachment
    ];
  const showOverhang = (
    layerConfig[LayerType.encroachment] as EncroachmentLayerConfig
  ).showOverhang;

  const { showHazardTrees, showDecliningTrees } = layerConfig[
    LayerType.trees
  ] as RiskTypeConfig;

  const deckGl = useRef<DeckGLRef<CustomMapView>>(null);

  // Track whether we've already set the initial viewport
  const shouldUpdateViewState = useRef(true);

  // The client map config the camera was last framed for. The initial context
  // is hydrated from the session cookie; when the live client context loads (or
  // the client switches in place) the config changes, and we re-arm so the
  // camera reframes to it rather than staying on the cookie's snapshot. Keyed on
  // the config value (not the client code): the cookie and the fresh
  // `/v1/client` share the same code, so a code key would never re-arm.
  const lastFramedViewState = useRef(JSON.stringify(context.viewState));

  // State is used for the data attribute on the map wrapper for testing
  const [initialViewportReady, setInitialViewportReady] = useState(false);

  const setViewportFromAddress = useCallback(() => {
    const parsed = parseUrlViewState(searchParams);
    if (!parsed) return;
    dispatch(
      updateViewState({
        latitude: parsed.latitude,
        longitude: parsed.longitude,
        zoom: parsed.zoom,
      })
    );
    setLocationPin({ lat: parsed.latitude, lng: parsed.longitude });
  }, [dispatch, searchParams]);

  useEffect(() => {
    if (location.pathname.includes(ADDRESS_PATH)) {
      setViewportFromAddress();
    }
  }, [location.pathname, setViewportFromAddress]);

  // Update the view state when the data changes, to match the bbox.
  // This will override the user's view if they've moved the map,
  // and will override the initial view state set in deckglUtils.ts.
  // Runs before paint so a client switch frames the new client in the first
  // committed frame, rather than painting the old client's camera for a beat.
  useLayoutEffect(() => {
    // Re-arm in the same layout pass as the fit: the initial context is the
    // session-cookie snapshot, and the live `/v1/client` load replaces it with
    // the client's current map config — reframe whenever that value changes.
    if (lastFramedViewState.current !== JSON.stringify(context.viewState)) {
      lastFramedViewState.current = JSON.stringify(context.viewState);
      shouldUpdateViewState.current = true;
    }

    // The view mode is briefly UNINITIALIZED before `useUrlSync` resolves it.
    // Bail without disarming so the pending initial fit still runs once the mode
    // settles — otherwise this transient state consumes the flag and the camera
    // never frames the client.
    if (currentViewMode.mode === ViewModeOptions.UNINITIALIZED) {
      return;
    }

    if (location.pathname.includes(ADDRESS_PATH)) {
      shouldUpdateViewState.current = false;
      setInitialViewportReady(true);
      return;
    }

    // Projects view modes handle the view via the useUrlSync hook and don't need this
    if (currentViewMode.mode !== ViewModeOptions.LEGACY) {
      shouldUpdateViewState.current = false;
      setInitialViewportReady(true);
      return;
    }

    // Frame the active client on first load and on every client switch, always
    // from the live client context (not the once-captured session metadata).
    // Fit the client's bbox when present; otherwise fall back to its configured
    // center/zoom so a client without a bbox still reframes off the stale one.
    if (shouldUpdateViewState.current) {
      shouldUpdateViewState.current = false;
      const { bbox, longitude, latitude, zoom } = context.viewState;
      // The Redux viewState carries no width/height until deck.gl's first
      // onViewStateChange merges them in, and updateViewport silently no-ops
      // without dimensions — which would re-dispatch the stale session-cookie
      // camera instead of fitting the bbox. Supply the window dimensions (the
      // same values MapView passes to <DeckGL width/height>) so the fit runs.
      const updatedViewState = bbox
        ? updateViewport(
            { ...viewState, width, height },
            bbox,
            widthRef.current,
            DESKTOP_VIEWBOX_PADDING,
            MOBILE_VIEWBOX_PADDING
          )
        : { ...viewState, longitude, latitude, zoom };

      dispatch(updateViewState(updatedViewState));
      setInitialViewportReady(true);
    }
  }, [
    shouldUpdateViewState,
    location.pathname,
    dispatch,
    viewState,
    width,
    height,
    currentViewMode.mode,
    context.code,
    context.viewState,
  ]);

  const map = useMemo(() => {
    const mapboxToken = getAppConfig().mapboxToken;
    let coordinates: number[] = [0, 0];
    let popupData: BoundingBoxCoordinatesReturn | undefined;

    if (selectedItem?.object) {
      const isScanLayer = selectedItem.layer?.id === LayerType[LayerType.scan];

      if (selectedItem.object.bbox) {
        if (isScanLayer) {
          // For spans, place the pin at the bbox center (same as hover logic)
          const [minLng, minLat, maxLng, maxLat] = selectedItem.object.bbox;
          coordinates = [(minLng + maxLng) / 2, (minLat + maxLat) / 2];
        } else {
          // For other layers, use viewport-aware anchor for the popup
          popupData = getBoundingBoxCoordinates(
            viewState,
            selectedItem.object,
            popupHeight,
            openLayerManager
          );
          coordinates = popupData.coordinates;
        }
      } else {
        if (selectedItem.coordinate) {
          coordinates = selectedItem.coordinate;
        } else {
          coordinates = getCentroid(
            getGeometryPositions(selectedItem.object.geometry)
          );
        }
      }
    }

    return (
      <MapView
        width={width}
        height={height}
        mapboxToken={mapboxToken}
        deckGl={deckGl}
        selectedItem={selectedItem}
        layerConfig={layerConfig}
        selectedTreeCover={selectedTreeCover}
        anchor={popupData?.anchor}
        popupOffset={popupData?.popupOffset}
        popupHeight={popupHeight}
        setPopupHeight={setPopupHeight}
        clickInfo={{ x: coordinates[0], y: coordinates[1] }}
        treesData={treesData}
        setTreesData={setTreesData}
        treesAggregateData={treesAggregateData}
        setTreesAggregateData={setTreesAggregateData}
        setSatelliteMetadata={setSatelliteMetadata}
        satelliteMetadata={satelliteMetadata}
        satelliteImageMetadata={satelliteImageMetadata}
        pickAndSetSatelliteMetadata={pickAndSetSatelliteMetadata}
        contextMenu={contextMenu}
        setContextMenu={setContextMenu}
        pageDimensions={pageDimensions}
        setPageDimensions={setPageDimensions}
        locationPin={locationPin}
        setLocationPin={setLocationPin}
        redFlagWarnings={redFlagWarnings}
        initialViewportReady={initialViewportReady}
      />
    );
    // This is a legacy dependency list.
    // We have kept it to avoid breaking existing functionality,
    // but it may be a source of bugs in the future.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    viewState,
    selectedItem,
    location.pathname,
    layerConfig,
    treesData,
    treesAggregateData,
    showHazardTrees,
    showDecliningTrees,
    contextMenu,
    pageDimensions,
    locationPin,
    satelliteMetadata,
    width,
    height,
    redFlagWarnings,
  ]);

  const { itemSelected } = useItemSelection(
    location.pathname,
    selectedItem?.path,
    viewState,
    openLayerManager
  );

  if (itemSelected) {
    dispatch(updateViewState(itemSelected));
    TagManager.dataLayer({
      dataLayer: {
        event: getGoogleEventForAnalysis(
          selectedAnalysis,
          GoogleEvents.spanClicked
        ),
      },
    });
  }

  const widthRef = useRef(width);
  useEffect(() => {
    widthRef.current = width;
  }, [width]);

  const popover = useMemo(() => {
    if (selectedItem) {
      return <MapPopover target={selectedItem} />;
    }
  }, [selectedItem]);

  const handleClick = useCallback(
    (e: MouseEvent) => {
      if (!(e.target instanceof HTMLElement)) return; // Ensure target is an HTML element

      //? What are we trying to do in this section? I've added the address path exception, but I don't know why the other conditions exist.
      // The context menu and location pin seem to clear just fine without this.
      // Regardless, it seems like we wouldn't want to clear stuff on pan(?) but this doesn't distinguish a pan from a click.
      //? It looks like maybe we're trying to clear the map if we click outside the map (ignore `mapbox-map` clicks),
      // but `e.target.id` is not set to `mapbox-map` in this case, so perhaps I'm misinterpreting the intent.
      if (
        e.target.id === 'location-link' ||
        e.target.id === 'mapbox-map' ||
        location.pathname.includes(ADDRESS_PATH)
      ) {
        return;
      }

      setContextMenu(undefined);
      setLocationPin(undefined);
    },
    [location.pathname]
  );

  // Adding window listener to remove the context menu on click
  useEffect(() => {
    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [handleClick]);

  const renderListByViewMode = () => {
    switch (currentViewMode.mode) {
      case ViewModeOptions.CUSTOM_LIST_DETAIL:
      case ViewModeOptions.CUSTOM_LIST_SPAN:
        return (
          <ErrorBoundary
            userErrorMessage={intl.formatMessage({
              id: 'customList.loadError',
            })}
          >
            <CustomListDetailPane />
          </ErrorBoundary>
        );
      case ViewModeOptions.CUSTOM_LIST_OVERVIEW:
        return (
          <ErrorBoundary
            userErrorMessage={intl.formatMessage({
              id: 'customList.loadError',
            })}
          >
            <CustomListOverviewPane />
          </ErrorBoundary>
        );
      default:
        return (
          <AnalysisList
            shouldShowRedFlagWarnings={shouldShowRedFlagWarnings}
            setRedFlagWarnings={setRedFlagWarnings}
          />
        );
    }
  };

  return (
    <PageLayout selected="Map" contentOverflow="visible">
      <DevModeDetector />
      <div
        className={`${classes.container} ${isListView ? classes.listView : ''}`}
        data-testid="analysis-grid-container"
      >
        {!isMobileInspectMode &&
          !isSmallScreen &&
          (currentViewMode.mode === ViewModeOptions.LEGACY ||
            isInProjectUrl(currentViewMode)) && (
            <div className={classes.header}>
              <ObjectiveDropdown
                layerConfig={layerConfig}
                showGroupBy={
                  currentViewMode.mode === ViewModeOptions.LEGACY &&
                  !activeGroupName
                }
              />
              {isNarrowScreen ? (
                <Button
                  data-testid="header-search-button"
                  aria-label={intl.formatMessage({ id: 'search.placeholder' })}
                  className={classes.searchButton}
                  variant="default"
                  onClick={() => {
                    navigateWithSearch('/search', { sessionOnly: true });
                  }}
                >
                  <Icon Component={Search} size={16} />
                </Button>
              ) : (
                <div className={classes.searchBar}>
                  <SearchBar
                    mapCenter={{
                      lat: viewState.latitude,
                      lng: viewState.longitude,
                    }}
                    bbox={customerMapBbox}
                  />
                </div>
              )}
              <FeatureBar
                shouldShowRedFlagWarnings={shouldShowRedFlagWarnings}
                setRedFlagWarnings={setRedFlagWarnings}
              />
            </div>
          )}

        {isSmallScreen && !isListView && (
          <FeatureBar
            shouldShowRedFlagWarnings={shouldShowRedFlagWarnings}
            setRedFlagWarnings={setRedFlagWarnings}
          />
        )}
        {isMobileInspectMode && isTouchDevice && (
          <div
            className={classes.closeInspectMode}
            onClick={() => {
              dispatch(updateInspectMode(false));
            }}
          >
            <FloatingIconButton Component={X} />
          </div>
        )}
        <div className={classes.banner}>
          <InternalReleaseBanner />
        </div>
        <div className={classes.map}>{map}</div>
        {/* Hide the span detail drawer on mobile when list view is active,
            otherwise the collapsed drawer preview bleeds through behind the list. */}
        {isSmallScreen && !isMobileInspectMode && !isListView && (
          <div className={classes.popover}>{popover}</div>
        )}
        {!isMobileInspectMode && (
          <>
            {(!isListView || !isSmallScreen) && (
              <ActionBar
                openLayerManager={openLayerManager}
                setOpenLayerManager={setOpenLayerManager}
                style={
                  isSmallScreen && selectedItem?.object
                    ? { marginBottom: 100 }
                    : undefined
                }
              />
            )}

            {!isSmallScreen && (
              <div className={classes.filters}>
                {currentViewMode.mode === ViewModeOptions.LEGACY && (
                  <FilterButtonsNetworkGroups />
                )}
                <FilterButton
                  isMoreFilters={false}
                  openFilterForm={() => dispatch(openFilterForm())}
                />
              </div>
            )}

            {isListView && isSmallScreen && <Overlay zIndex={0} />}
            {isSpanSelectedOnDesktop ? (
              <SpanDetailSidebar selectedItem={selectedItem} />
            ) : null}
          </>
        )}
        {/*
          Keep the list always mounted so that AnalysisList's isFirstRender ref
          stays false. Unmounting (via span sidebar or inspect mode) and
          remounting would trigger a viewport zoom reset.
          display:contents makes the wrapper invisible to CSS grid when visible.
        */}
        <div
          style={{
            display:
              isSpanSelectedOnDesktop || isMobileInspectMode
                ? 'none'
                : 'contents',
          }}
        >
          {renderListByViewMode()}
        </div>

        {!isSmallScreen && !isMobileInspectMode && (
          <MapLegend
            layerConfig={layerConfig}
            showOverhang={showOverhang}
            zoom={mapState.zoom}
            selectedTreeCover={selectedTreeCover}
            selectedAnalysis={selectedAnalysis}
          />
        )}

        <Filter
          key={currentViewMode.mode} // Force a re-render of the filter when the mode changes to keep clean separation.
          onClose={() => {
            if (currentViewMode.mode === ViewModeOptions.LEGACY) {
              navigate(
                { pathname: '/', search: searchParams.toString() },
                { replace: true }
              );
            }
            dispatch(closeFilterForm());
          }}
          redFlagWarnings={redFlagWarnings}
        />

        <WorklogAddOrEdit />

        {satelliteImageMetadata ? (
          <StatusBar
            imageDate={satelliteImageMetadata.date}
            imageProvider={satelliteImageMetadata.provider}
            showImageryDate={satelliteImageMetadata.showImageryDate}
          />
        ) : null}
      </div>
    </PageLayout>
  );
};
